import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Drawer, Plus, Select, Spinner, TextInput, Trash01 } from '@opendate-app/setlist';
import Flatpickr from 'react-flatpickr';
import { offsetDateForBrowser, offsetDateForServer } from '../../../shared/timeZoneLogic';
import './TicketDrawer.css';

const GeneralAdmissionHoldModal = ({
  open,
  onClose,
  title,
  initialHold,
  ticketTypes,
  addOns,
  onSubmit,
  renderRelease,
  quantityHint,
  allowMultipleKinds
}) => {
  const blankRow = (holdableType = 'TicketType') => ({ id: null, holdable_type: holdableType, holdable_id: null, quantity: '' });
  const [hold, setHold] = useState({ name: '', release_at: null });
  const [rows, setRows] = useState([blankRow()]);
  const [removedIds, setRemovedIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    
    setRemovedIds([]);
    if (initialHold) {
      const items = initialHold.general_admission_hold_items || [];
      setRows(items.length
        ? items.map(item => ({ id: item.id || null, holdable_type: item.holdable_type || 'TicketType', holdable_id: item.holdable_id || null, quantity: item.quantity || 0 }))
        : [blankRow()]);
      setHold({
        name: initialHold.name || '',
        release_at: initialHold.release_at ? offsetDateForBrowser(initialHold.release_at) : null,
        release_offset_minutes: initialHold.release_offset_minutes ?? null
      });
    } else {
      setRows([blankRow()]);
      setHold({
        name: '',
        release_at: null,
        release_offset_minutes: null
      });
    }
  }, [open, initialHold?.id]);

  const ticketTypeOptions = (ticketTypes || []).map(tt => ({
    value: tt.id,
    label: tt.name
  }));

  const addOnOptions = (addOns || []).filter(addOn => !addOn.seating_chart_category_key).map(addOn => ({
    value: addOn.id,
    label: addOn.name
  }));
  const ticketRowCount = rows.filter(row => row.holdable_type === 'TicketType').length;
  const addOnRowCount = rows.length - ticketRowCount;

  const updateRow = (index, changes) => setRows(prev => prev.map((row, i) => (i === index ? { ...row, ...changes } : row)));
  const removeRow = (index) => {
    const row = rows[index];
    if (row.id) setRemovedIds(prev => [...prev, row.id]);
    setRows(prev => prev.filter((_, i) => i !== index));
  };

  const isSaveDisabled = isSaving || !hold.name || ticketRowCount === 0 ||
    rows.some(row => !row.holdable_id || !(parseInt(row.quantity) > 0));

  const handleSave = () => {
    if (isSaveDisabled) return;
    setIsSaving(true);
    
    const payload = {
      name: hold.name,
      ...(renderRelease
        ? { release_offset_minutes: hold.release_offset_minutes }
        : { release_at: hold.release_at ? offsetDateForServer(hold.release_at) : null }),
      general_admission_hold_items_attributes: [
        ...rows.map(row => ({
          ...(row.id && { id: row.id }),
          holdable_type: row.holdable_type,
          holdable_id: row.holdable_id,
          quantity: parseInt(row.quantity)
        })),
        ...removedIds.map(id => ({ id, _destroy: true }))
      ]
    };

    onSubmit(payload)
      .then(() => {
        onClose();
      })
      .catch(() => {
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <Drawer
      data-above-sidebar
      data-lift-conductor-orb={open || undefined}
      open={open}
      onClose={onClose}
      title={title}
      width={600}
      backdrop
      footer={(
        <div className="d-flex justify-content-end" style={{ gap: 12 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={isSaveDisabled} onClick={handleSave}>
            {isSaving ? <Spinner size="sm" color="currentColor" aria-label="Saving" /> : 'Save'}
          </Button>
        </div>
      )}
    >
      <div className="mb-3">
        <div style={{ fontSize: '18px', fontWeight: 600, color: 'var(--sl-text-primary)' }}>Basic Details</div>
        <div style={{ fontSize: '14px', color: 'var(--sl-text-tertiary)' }}>Essential information regarding this hold.</div>
      </div>

      <div className="mb-3">
        <TextInput
          label="Hold Name"
          required
          placeholder="e.g., Press Hold, VIP Hold"
          value={hold.name}
          onChange={e => setHold(prev => ({ ...prev, name: e.target.value }))}
        />
      </div>

      <div className="mt-4 mb-3">
        <div style={{ fontSize: '18px', fontWeight: 600, color: 'var(--sl-text-primary)' }}>Inventory & Duration</div>
        <div style={{ fontSize: '14px', color: 'var(--sl-text-tertiary)' }}>What this hold sets aside, tickets and add-ons, and an optional release date that can be set for this hold.</div>
      </div>

      {rows.map((row, index) => {
        const isAddOn = row.holdable_type === 'AddOn';
        const options = isAddOn ? addOnOptions : ticketTypeOptions;
        return (
        <div className="form-row align-items-end mb-3" key={row.id || `new-${index}`}>
          <div className="col">
            <Select
              label={isAddOn ? 'Add-on' : 'Ticket type'}
              required
              placeholder={isAddOn ? 'Select add-on' : 'Select ticket type'}
              options={options.filter(option => option.value === row.holdable_id || !rows.some(other => other.holdable_type === row.holdable_type && other.holdable_id === option.value))}
              value={row.holdable_id}
              onChange={(value) => updateRow(index, { holdable_id: value || null })}
              clearable
            />
          </div>
          <div className="col-4">
            <TextInput
              label={isAddOn ? 'Add-ons Held' : 'Tickets Held'}
              required
              type="number"
              placeholder="0"
              min="0"
              value={row.quantity}
              onChange={e => updateRow(index, { quantity: e.target.value })}
              hint={isAddOn ? null : quantityHint}
            />
          </div>
          {(isAddOn || ticketRowCount > 1) && (
            <div className="col-auto">
              <Button variant="tertiary" icon={Trash01} aria-label={isAddOn ? 'Remove add-on' : 'Remove ticket type'} onClick={() => removeRow(index)} />
            </div>
          )}
        </div>
        );
      })}

      {allowMultipleKinds && (ticketRowCount < ticketTypeOptions.length || addOnRowCount < addOnOptions.length) && (
        <div className="mb-3 d-flex" style={{ gap: 12 }}>
          {ticketRowCount < ticketTypeOptions.length && (
            <Button variant="tertiary" icon={Plus} onClick={() => setRows(prev => [...prev, blankRow()])}>
              Add ticket type
            </Button>
          )}
          {addOnRowCount < addOnOptions.length && (
            <Button variant="tertiary" icon={Plus} onClick={() => setRows(prev => [...prev, blankRow('AddOn')])}>
              Add add-on
            </Button>
          )}
        </div>
      )}

      <div className="form-row">
        <div className="col-6">
          {renderRelease ? renderRelease(hold.release_offset_minutes, (minutes) => setHold(prev => ({ ...prev, release_offset_minutes: minutes }))) : (
            <>
              <label className="mb-1" style={{ fontSize: '14px', color: 'var(--sl-text-secondary)' }}>Release Date</label>
              <Flatpickr
                options={{ enableTime: true, dateFormat: 'F j, Y - h:i K', minDate: new Date() }}
                placeholder="Select date / time"
                className="form-control form-control-flatpickr"
                value={hold.release_at || ''}
                onClose={(dates) => setHold(prev => ({ ...prev, release_at: dates[0] }))}
              />
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
};

GeneralAdmissionHoldModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  initialHold: PropTypes.object,
  ticketTypes: PropTypes.array.isRequired,
  addOns: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  renderRelease: PropTypes.func,
  quantityHint: PropTypes.string,
  allowMultipleKinds: PropTypes.bool
};

GeneralAdmissionHoldModal.defaultProps = {
  initialHold: null,
  addOns: [],
  renderRelease: null,
  quantityHint: null,
  allowMultipleKinds: true
};

export default GeneralAdmissionHoldModal;
